// Mirrors internal-api `ContentTracking*` (apps/internal-api/src/model/contentTracking).
// Slots are display positions; each video's stored mandatory flag is independent of its position.

export type ContentTrackingFieldSource = 'manual' | 'connected' | 'stored' | null;

export interface ContentTrackingFieldEvidence {
  source: ContentTrackingFieldSource;
  observedAt: string | null;
}

export interface ContentTrackingVideoEvidence {
  views: ContentTrackingFieldEvidence;
  likes: ContentTrackingFieldEvidence;
  comments: ContentTrackingFieldEvidence;
  saves: ContentTrackingFieldEvidence;
  caption: ContentTrackingFieldEvidence;
  postedAt: ContentTrackingFieldEvidence;
}

export type ContentTrackingVideoSource = 'submitted' | 'legacy' | 'administrator';

export interface ContentTrackingVideo {
  url: string;
  platform: string;
  postId?: string;
  mandatory: boolean;
  submissionMandatory: boolean;
  hasSubmission: boolean;
  slot: number;
  submittedAt: string;
  sparkAdsCode?: string;
  caption: string | null;
  postedAt: string | null;
  fieldEvidence: ContentTrackingVideoEvidence;
  views: number | null;
  likes: number | null;
  comments: number | null;
  saves: number | null;
  engagementRate: number | null;
  collectedAt: string | null;
  metricsAvailability: 'available' | 'unavailable';
  topVideo: boolean;
  source: ContentTrackingVideoSource;
}

export interface ContentTrackingMetadata {
  revision: string | null;
  notes: string;
  cancelled: boolean;
  cancelReason: string;
  topVideoUrls: string[];
}

export interface ContentTrackingRow {
  influencerId: string;
  userId?: number;
  handle: string | null;
  displayName: string | null;
  email: string | null;
  platform: string | null;
  stage: string;
  rejected: boolean;
  shippingTracking: string | null;
  trackingUrl: string | null;
  orderId: string | null;
  packageStatus: string | null;
  packageStatusUpdatedAt: string | null;
  packageCarrier: string | null;
  shipmentAvailability: 'available' | 'unavailable';
  deliveredAt: string | null;
  contentVersion: string;
  contentIssues: string[];
  metadata: ContentTrackingMetadata;
  requiredVideo: ContentTrackingVideo | null;
  videos: ContentTrackingVideo[];
}

export interface ContentTrackingCounts {
  total: number;
  matching: number;
  stages: Record<string, number>;
  hasLink: number;
  noLink: number;
  hasTopVideo: number;
  sparkMissing: number;
  platforms: Record<string, number>;
}

export interface ContentTrackingPageInfo {
  limit: number;
  hasMore: boolean;
  nextCursor: string | null;
}

export interface ContentTrackingCapabilities {
  packageStatusFilter: boolean;
  shipmentTracking: boolean;
  metricsRefresh?: boolean;
  searchFields: string[];
  exactEmailFilter: boolean;
  metricsPostLimit: number;
}

export interface ContentTrackingPage {
  rows: ContentTrackingRow[];
  counts: ContentTrackingCounts;
  pageInfo: ContentTrackingPageInfo;
  capabilities: ContentTrackingCapabilities;
}

export type ContentTrackingStageFilter =
  | 'Shortlisted'
  | 'Shipped'
  | 'Received'
  | 'Published'
  | 'Completed'
  | 'Cancelled';
export type ContentTrackingPlatformFilter = 'tiktok' | 'instagram';
export type ContentTrackingLinkFilter = 'hasLink' | 'noLink';

export interface ContentTrackingListParams {
  limit?: number;
  cursor?: string;
  stage?: ContentTrackingStageFilter;
  platform?: ContentTrackingPlatformFilter;
  search?: string;
  email?: string;
  contentLink?: ContentTrackingLinkFilter;
  sparkCode?: 'missing';
  topVideo?: 'hasTopVideo';
}

export type ContentTrackingExportPurpose = 'reminders' | 'report';

export interface ContentTrackingReportVideo {
  slot: number;
  mandatory: boolean;
  caption: string | null;
  postedAt: string | null;
  fieldEvidence: ContentTrackingVideoEvidence;
  url: string;
  platform: string;
  views: number | null;
  likes: number | null;
  comments: number | null;
  saves: number | null;
  engagementRate: number | null;
  collectedAt: string | null;
  metricsAvailability: 'available' | 'unavailable';
  topVideo: boolean;
}

export interface ContentTrackingExportRow {
  influencerId: string;
  handle: string | null;
  platform: string | null;
  stage: string;
  email?: string | null;
  shippingTracking?: string | null;
  trackingUrl?: string | null;
  packageStatus?: string | null;
  videos?: ContentTrackingReportVideo[];
}

export interface ContentTrackingExportPage {
  rows: ContentTrackingExportRow[];
  pageInfo: ContentTrackingPageInfo;
  totalMatching: number;
}

export interface ContentTrackingMetadataRequest {
  expectedRevision: string | null;
  expectedContentVersion: string;
  notes: string;
  cancelled: boolean;
  cancelReason: string;
  topVideoUrls: string[];
}

export interface ContentTrackingSparkRequest {
  expectedContentVersion: string;
  url: string;
  sparkAdsCode?: string;
}

export interface ContentTrackingVideoRequest {
  expectedContentVersion: string;
  url: string;
  caption?: string;
  postedAt?: string;
  views?: number;
  likes?: number;
  comments?: number;
  saves?: number;
}

export interface ContentTrackingVideoResult {
  contentVersion: string;
  video: Omit<ContentTrackingVideo, 'topVideo'>;
  connectedObservationReason: string | null;
}

export interface ContentTrackingRefreshRequest {
  limit?: number;
  cursor?: string;
  skipObservedWithinHours?: number;
}

export type ContentTrackingRefreshStatus = 'refreshed' | 'skipped' | 'unavailable' | 'conflict' | 'failed';

export interface ContentTrackingRefreshCreator {
  influencerId: string;
  status: ContentTrackingRefreshStatus;
  reason: string | null;
  matchedVideos: number;
}

export interface ContentTrackingRefreshResult {
  processed: number;
  refreshed: number;
  creators: ContentTrackingRefreshCreator[];
  pageInfo: ContentTrackingPageInfo;
}

export const CONTENT_TRACKING_STAGE_FILTERS: ContentTrackingStageFilter[] = [
  'Shortlisted',
  'Shipped',
  'Received',
  'Published',
  'Completed',
  'Cancelled',
];

export const CONTENT_TRACKING_PAGE_LIMIT = 100;
